import React, { ReactNode } from 'react';
import I18n from 'modules/i18n';
import { tv } from 'tailwind-variants';
import { BorderColors, CardType } from '@/types/api/LessonOverview';
import cn from '@/utils/cn';
import useFlags from '@/hooks/useFlags';
import baseActionBarStyles from './ActionBar/styles';
import { CARD_ROOT_REFLOW } from './reflowStyles';
import styles from './index.module.scss';

const actionBarStyles = tv({
  extend: baseActionBarStyles,
  slots: {
    bar: 'tw-flex tw-items-center',
  },
});

const cardBodyStyles = tv({
  slots: {
    body: styles.body,
    description: styles.description,
    header: styles.header,
    img: '',
  },
  variants: {
    reflow: {
      true: {
        body: 'tw-h-auto tw-min-h-[7.5rem] reflow-narrow:tw-h-[120px]',
        description: '[&_a]:tw-whitespace-normal [&_a]:tw-overflow-visible [&_a]:tw-h-auto reflow-narrow:[&_a]:tw-whitespace-nowrap reflow-narrow:[&_a]:tw-overflow-hidden reflow-narrow:[&_a]:tw-h-5',
        header: 'tw-h-auto tw-min-h-[6.875rem] reflow-narrow:tw-h-[110px]',
        img: 'tw-max-w-full reflow-narrow:tw-max-w-none',
      },
    },
  },
});

const cardStyles = tv({
  base: `card ${styles.card}`,
  variants: {
    archived: {
      true: 'archived',
    },
    reflow: {
      true: CARD_ROOT_REFLOW,
    },
  },
});

type ActionBarProps = {
  children?: React.ReactNode,
};

export const ActionBar = ({ children }: ActionBarProps) => {
  const { accessibilityReflow } = useFlags();
  const { actions, bar } = actionBarStyles({ reflow: accessibilityReflow });

  return (
    <div className={bar()}>
      <div className={actions()}>
        {children}
      </div>
    </div>
  );
};

type CardBodyProps = {
  ariaLabel: string,
  children: ReactChildren,
  iconClassNames?: string,
  iconSrc: string,
  linkLabel: string,
  linkHref: string,
  lowerIcon?: ReactNode,
  openInNewWindow?: boolean,
};

export const CardBody = ({
  ariaLabel,
  children,
  iconClassNames,
  iconSrc,
  linkHref,
  linkLabel,
  lowerIcon,
  openInNewWindow,
}: CardBodyProps) => {
  const target = openInNewWindow ? '_blank' : '';
  const { accessibilityReflow } = useFlags();
  const { body, description, header, img } = cardBodyStyles({ reflow: accessibilityReflow });

  return (
    <div className="tw-bg-white">
      <a
        aria-hidden
        aria-label={ariaLabel}
        href={linkHref}
        tabIndex={-1}
        target={target}
      >
        <div className={header()}>
          <img
            alt={ariaLabel}
            className={cn(styles.img, iconClassNames, img())}
            src={iconSrc}
          />
          {lowerIcon && <div className={styles.lowerIcon}>{lowerIcon}</div>}
        </div>
      </a>
      <div className={body()}>
        <div className={styles.title}>
          <a
            aria-label={linkLabel}
            className={styles.titleLink}
            href={linkHref}
            target={target}
          >
            {linkLabel}
          </a>
        </div>

        <div className={description()}>
          {children}
        </div>
      </div>
    </div>
  );
};

CardBody.defaultProps = {
  openInNewWindow: false,
};

type CardBaseProps = {
  archived?: boolean,
  bannerBorderColor: BorderColors,
  paddedLabel?: boolean,
  type: string,
  children: ReactChildren
};

// Exactly one banner label: `label` is an i18n key, `labelText` is already-localized text for labels
// that aren't a fixed key (library names come from tag data, not the locale files). The union keeps a
// card that supplies neither from compiling — it would render `undefined` into the banner.
type CardProps = CardBaseProps & (
  { label: CardType, labelText?: never } |
  { label?: never, labelText: string }
);

const Card = (
  { archived = false, bannerBorderColor, children, label, labelText, paddedLabel = false, type }: CardProps,
) => {
  // AIDEV-NOTE: the union above should make this unreachable, but `tsc` can't check this project (the
  // installed TS can't parse date-fns' .d.ts, and a syntax error suppresses every semantic diagnostic),
  // so don't let a card with no label throw and take the whole lesson overview down with it.
  const formattedLabel = (labelText || (label && I18n.t(label)) || '').toUpperCase();
  const { accessibilityReflow } = useFlags();

  return (
    <section
      aria-label={`${type}-card`}
      className={cardStyles({ archived, reflow: accessibilityReflow })}
      id={`${type}-card-container`}
    >
      <div className={`${styles.banner} ${styles[bannerBorderColor]}`}>
        {paddedLabel
          ? <div className="tw-px-2">{formattedLabel}</div>
          : formattedLabel
        }
      </div>
      {children}
    </section>
  );
};

export default Card;
